import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'

/* ─────────────────────────────────────────────────────────────
   "Become an Astrologer" — is the prompt on for THIS account?

   GET /astrologer/onboarding answers it server-side: the admin
   sets a global switch, an audience of roles, and per-account
   allow/deny exceptions (admin panel → Settings → Feature
   Visibility), and the API applies all three. The dashboard
   never decides on its own — it only renders the answer, so a
   role list changed in the admin panel needs no site deploy.

   Best-effort, like every other dashboard read: an API that is
   down or an older backend without the endpoint simply means no
   prompt (`canApply` false), never a broken dashboard.
   ───────────────────────────────────────────────────────────── */

const QUERY_KEY = ['dashboard', 'astrologer-onboarding']

export const astrologerOnboardingKey = QUERY_KEY

export function useAstrologerOnboarding() {
  const { user } = useAuth()

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.astrologers.onboarding(),
    enabled: Boolean(user?.id),
    retry: false,
    // The admin can flip this at any time, and it decides whether a sidebar
    // entry exists — short enough to follow a change, long enough not to refetch
    // on every tab switch.
    staleTime: 2 * 60 * 1000,
    select: (res) => res?.data ?? {},
  })

  const data = query.data ?? {}
  return {
    /** Offer the form: the admin shows it to this account AND it hasn't applied. */
    canApply: Boolean(data.can_apply),
    /** The admin's answer alone, regardless of whether they've already applied. */
    visible: Boolean(data.visible),
    /** Non-null once submitted: { status, is_live, submitted_at … }. */
    application: data.application ?? null,
    /** Show the tab at all: either the form or the state of their application. */
    hasEntry: Boolean(data.can_apply || data.application),
    isPending: query.isPending,
    isError: query.isError,
  }
}
